/**
 * FlatLayButton — Bouton autonome "🎨 Flat-lay" + modal viewer + share.
 *
 * Usage simple à insérer à côté du bouton Try-on dans tenue du jour et
 * sélection styliste (qui ne passent pas par OutfitCard).
 *
 *   <FlatLayButton outfit={homeOutfit} />
 *
 * Comportement :
 *   - Si pas de flat-lay déjà persisté pour cette tenue → bouton "🎨 Flat-lay"
 *     → clic → lance Vertex (10-15s) → modal s'ouvre avec l'image
 *   - Si déjà persisté → bouton "🎨 Voir flat-lay" → clic → modal direct
 *   - Modal : image plein cadre + ✕ + ⇪ Partager
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, Image, TouchableOpacity, Modal, ActivityIndicator, StyleSheet,
  Pressable, Platform, Dimensions, DeviceEventEmitter, Alert, StatusBar,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useI18n } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { showAppAlert } from './AppDialog';
import { getResultsForOutfit, buildOutfitKey } from '../services/TryOnResultsService';
import { generateFlatLay } from '../services/FlatLayService';
import { shareImage } from '../services/ShareService';
import { showProcessing, hideProcessing } from './ProcessingOverlay';
import { FLATLAY_BACKGROUNDS, DEFAULT_FLATLAY_BG, FLATLAY_BG_STORAGE_KEY, isValidFlatLayBg } from '../config/flatlayBackgrounds';

const GOLD = '#D4AF72';
const screen = Dimensions.get('window');

export default function FlatLayButton({ outfit, style, compact = false }) {
  const { t, lang } = useI18n();
  const insets = useSafeAreaInsets(); // v1.13.7 — éviter que ✕/🔄/⇪ passent sous la barre de statut
  const { user } = useAuth() || {};
  const navigation = useNavigation();
  const [flatlayUrl, setFlatlayUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [open, setOpen] = useState(false);
  // v1.12.996 — fond choisi par l'utilisateur (persisté comme défaut)
  const [bg, setBg] = useState(DEFAULT_FLATLAY_BG);
  // v1.12.997 — sélecteur de fond AVANT génération
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    AsyncStorage.getItem(FLATLAY_BG_STORAGE_KEY)
      .then((v) => { if (mounted && isValidFlatLayBg(v)) setBg(v); })
      .catch(() => {});
    getResultsForOutfit(outfit)
      .then((r) => { if (mounted) setFlatlayUrl(r?.flatlay || null); })
      .catch(() => {});
    const sub = DeviceEventEmitter.addListener('tryon_results_updated', (e) => {
      try {
        if (buildOutfitKey(outfit) === e?.outfitKey && e?.kind === 'flatlay') {
          setFlatlayUrl(e.imageUrl);
        }
      } catch {}
    });
    return () => { mounted = false; try { sub.remove(); } catch {} };
  }, [outfit]);

  const doGenerate = useCallback(async (bgOverride) => {
    if (generating) return;
    const useBg = bgOverride || bg;
    setGenerating(true);
    try { showProcessing(
      t('flat_lay_generating') || 'Génération de votre flat-lay…',
      t('flat_lay_generating_sub') || '~10-15 secondes — IA Vertex Gemini',
    ); } catch {}
    try {
      const r = await generateFlatLay(outfit, { background: useBg });
      setFlatlayUrl(r.imageUrl);
      setOpen(true);
      // v1.12.744 — Notifier que le quota try-on a augmenté (UI counter refresh).
      // Backend a déjà inséré dans tryon_usage. r.usage est le nouveau count.
      try {
        DeviceEventEmitter.emit('tryon_usage_changed', {
          count: r.usage,
          limit: r.limit,
          tier: r.tier,
          reason: 'flatlay',
        });
      } catch {}
    } catch (e) {
      if (e?.code === 'quota_exceeded') {
        // v1.12.744 — Quota dépassé → popup abonnement / achat (même flow que try-on)
        try {
          DeviceEventEmitter.emit('tryon_limit_reached', {
            tier:          e?.detail?.tier,
            limit:         e?.detail?.limit,
            current:       e?.detail?.usage,
            isPaidTier:    e?.detail?.isPaidTier,
            nextResetDate: e?.detail?.nextResetDate,
          });
        } catch {}
      } else {
        const msg = e?.message || t('flat_lay_error') || 'Échec génération flat-lay';
        try { Alert.alert(t('error') || 'Erreur', msg); } catch {}
      }
    } finally {
      setGenerating(false);
      try { hideProcessing(); } catch {}
    }
  }, [outfit, generating, t, bg]);

  // v1.12.997 — Choix d'un fond dans le sélecteur : persiste le défaut (PAS de
  // régénération auto — évite le crash Modal-in-Modal). La génération est lancée
  // par le bouton "Générer" du sélecteur via startGenerate().
  const chooseBg = useCallback((id) => {
    if (!isValidFlatLayBg(id)) return;
    setBg(id);
    try { AsyncStorage.setItem(FLATLAY_BG_STORAGE_KEY, id); } catch {}
  }, []);

  // v1.12.997 — Ferme le sélecteur PUIS lance la génération (jamais 2 modals
  // simultanés : showProcessing est un host Modal → conflit iOS = crash).
  const startGenerate = useCallback((id) => {
    setPickerOpen(false);
    const useBg = id || bg;
    setFlatlayUrl(null);
    setTimeout(() => { doGenerate(useBg); }, 160);
  }, [bg, doGenerate]);

  const handlePress = useCallback(async () => {
    try { require('../services/AnalyticsService').trackTap('ui_flatlay_tap', { state: !user ? 'logged_out' : flatlayUrl ? 'view_existing' : 'open_picker' }); } catch {}
    // v1.12.871 — Gate auth : user non connecté → popup invitation login
    if (!user) {
      showAppAlert(
        t('login_required') || 'Connexion requise',
        t('login_required_for_flatlay') || 'Connectez-vous pour générer un Flat-Lay de votre tenue.',
        [
          { text: t('cancel') || 'Annuler', style: 'cancel' },
          { text: t('sign_in') || 'Se connecter', onPress: () => {
            try { navigation.navigate('Login'); } catch (e) { console.warn('[FlatLayButton] nav Login:', e?.message); }
          }},
        ]
      );
      return;
    }
    if (flatlayUrl) {
      setOpen(true);
      return;
    }
    // v1.12.997 — choisir le fond AVANT de générer
    setPickerOpen(true);
  }, [flatlayUrl, user, t, navigation]);

  // Régénérer = rouvrir le sélecteur de fond (ferme le viewer d'abord)
  const handleRegenerate = useCallback(() => {
    setOpen(false);
    setTimeout(() => { setPickerOpen(true); }, 160);
  }, []);

  const handleShare = useCallback(async () => {
    if (!flatlayUrl) return;
    // v1.12.746 — Fermer le modal parent AVANT d'ouvrir ShareSheet
    // (sinon Modal-in-Modal sur web → freeze + sheet invisible)
    setOpen(false);
    // Petit délai pour laisser RN-Web démonter le modal parent proprement
    setTimeout(() => {
      try { shareImage(flatlayUrl); }
      catch (e) { console.warn('[FlatLayButton] share:', e?.message); }
    }, 80);
  }, [flatlayUrl]);

  const label = compact
    ? '🎨'
    : (flatlayUrl ? `🎨 Flat-Lay` : `🎨 Flat-Lay +`);

  return (
    <>
      <TouchableOpacity
        style={[
          s.btn,
          compact && s.btnCompact,
          style,
        ]}
        onPress={handlePress}
        activeOpacity={0.85}
        disabled={generating}
      >
        {generating ? (
          <ActivityIndicator size="small" color={GOLD} />
        ) : (
          <Text style={[s.btnText, compact && s.btnTextCompact]}>{label}</Text>
        )}
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={s.bg} onPress={() => setOpen(false)}>
          {generating && !flatlayUrl ? (
            <View style={s.loadingBox}>
              <ActivityIndicator size="large" color={GOLD} />
              <Text style={s.loadingText}>{t('flat_lay_generating') || 'Génération de votre flat-lay…'}</Text>
              <Text style={s.loadingSub}>{t('flat_lay_generating_sub') || '~10-15 secondes'}</Text>
            </View>
          ) : flatlayUrl ? (
            <Pressable onPress={(e) => e.stopPropagation()} style={{ width: '100%', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <Image source={{ uri: flatlayUrl }} style={s.image} resizeMode="contain" />
            </Pressable>
          ) : null}

          {flatlayUrl ? (
            <View style={[s.actions, { top: Math.max(insets.top, Platform.OS === 'ios' ? 50 : 24) + 10 }]} pointerEvents="box-none">
              <TouchableOpacity style={s.actionBtn} onPress={() => setOpen(false)}>
                <Text style={s.actionBtnText}>✕</Text>
              </TouchableOpacity>
              <TouchableOpacity style={s.actionBtn} onPress={handleRegenerate}>
                <Text style={s.actionBtnText}>🔄</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[s.actionBtn, s.actionBtnShare]} onPress={handleShare}>
                <Text style={s.actionBtnText}>⇪</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          {/* v1.12.997 — bouton "changer le fond" (rouvre le sélecteur) */}
          {flatlayUrl ? (
            <Pressable onPress={(e) => e.stopPropagation()} style={[s.bgBar, { bottom: (Platform.OS === 'ios' ? 40 : 16) + Math.max(insets.bottom, Platform.OS === 'android' ? 12 : 0) }]} pointerEvents="box-none">
              <TouchableOpacity style={s.changeBgBtn} onPress={handleRegenerate} activeOpacity={0.85}>
                <Text style={s.changeBgBtnText}>{lang === 'en' ? '🎨  Change background' : '🎨  Changer le fond'}</Text>
              </TouchableOpacity>
            </Pressable>
          ) : null}
        </Pressable>
      </Modal>

      {/* v1.12.997 — Sélecteur de fond AVANT génération (tuiles d'aperçu) */}
      <Modal visible={pickerOpen} transparent animationType="slide" onRequestClose={() => setPickerOpen(false)}>
        <Pressable style={s.pickerOverlay} onPress={() => setPickerOpen(false)}>
          {/* v1.13.74 — padding bas = inset système Android (barre de navigation)
              sinon le bouton "Annuler" / "Generate" passe SOUS la barre du bas. */}
          <Pressable style={[s.pickerSheet, { paddingBottom: (Platform.OS === 'ios' ? 36 : 16) + Math.max(insets.bottom, Platform.OS === 'android' ? 12 : 0) }]} onPress={(e) => e.stopPropagation()}>
            <View style={s.pickerHandle} />
            <Text style={s.pickerTitle}>{lang === 'en' ? 'Choose a background' : 'Choisissez un fond'}</Text>
            <Text style={s.pickerSub}>
              {lang === 'en' ? 'Your pieces will be laid out on this background.' : 'Vos pièces seront posées sur ce fond.'}
            </Text>
            <View style={s.tileGrid}>
              {FLATLAY_BACKGROUNDS.map((b) => {
                const active = b.id === bg;
                return (
                  <TouchableOpacity
                    key={b.id}
                    onPress={() => chooseBg(b.id)}
                    activeOpacity={0.85}
                    style={s.tileWrap}
                    accessibilityLabel={lang === 'en' ? b.labelEn : b.labelFr}
                  >
                    <View style={[s.tile, { backgroundColor: b.swatch }, active && s.tileActive]}>
                      {active ? (
                        <View style={s.tileCheckWrap}>
                          <Text style={[s.tileCheck, { color: b.id === 'slate' ? '#FFF' : '#1A1A1A' }]}>✓</Text>
                        </View>
                      ) : null}
                    </View>
                    <Text style={[s.tileLabel, active && s.tileLabelActive]} numberOfLines={1}>
                      {lang === 'en' ? b.labelEn : b.labelFr}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <TouchableOpacity style={s.generateBtn} onPress={() => startGenerate(bg)} activeOpacity={0.9} disabled={generating}>
              <Text style={s.generateBtnText}>
                {generating ? (lang === 'en' ? 'Generating…' : 'Génération…') : (lang === 'en' ? '✨  Generate Flat-Lay' : '✨  Générer le Flat-Lay')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.pickerCancel} onPress={() => setPickerOpen(false)} activeOpacity={0.7}>
              <Text style={s.pickerCancelText}>{lang === 'en' ? 'Cancel' : 'Annuler'}</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const s = StyleSheet.create({
  btn: {
    flex: 1,
    backgroundColor: '#1A1A1A',
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: GOLD,
    flexDirection: 'row',
    gap: 6,
  },
  btnCompact: {
    flex: 0,
    width: 44,
    height: 44,
    borderRadius: 22,
    paddingVertical: 0,
  },
  btnText: {
    fontSize: 12,
    fontWeight: '700',
    color: GOLD,
    letterSpacing: 1,
  },
  btnTextCompact: {
    fontSize: 18,
    letterSpacing: 0,
  },
  bg: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.94)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingBox: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 30,
  },
  loadingText: { color: GOLD, fontSize: 14, fontWeight: '600' },
  loadingSub: { color: '#888', fontSize: 11 },
  image: { width: screen.width, height: screen.height * 0.85 },
  actions: {
    position: 'absolute',
    // v1.12.984 — Android : décaler sous la barre de statut (sinon les boutons
    // chevauchent l'horloge/heure). iOS : 50 (notch).
    top: Platform.OS === 'ios' ? 50 : (StatusBar.currentHeight || 24) + 12,
    right: 16,
    flexDirection: 'row',
    gap: 10,
  },
  actionBtn: {
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center', justifyContent: 'center',
  },
  actionBtnShare: { backgroundColor: GOLD },
  actionBtnText: { color: '#FFF', fontSize: 20, fontWeight: '600' },
  // v1.12.997 — bouton "changer le fond" en bas du viewer
  bgBar: {
    position: 'absolute',
    bottom: Platform.OS === 'ios' ? 40 : 24,
    left: 0, right: 0,
    alignItems: 'center',
  },
  changeBgBtn: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.4)',
    paddingVertical: 10, paddingHorizontal: 20, borderRadius: 24,
  },
  changeBgBtnText: { color: '#FFF', fontSize: 13, fontWeight: '600', letterSpacing: 0.3 },
  // v1.12.997 — sélecteur de fond avant génération (bottom sheet)
  pickerOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  pickerSheet: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24, borderTopRightRadius: 24,
    paddingHorizontal: 20, paddingTop: 10,
    paddingBottom: Platform.OS === 'ios' ? 36 : 24,
  },
  pickerHandle: { width: 40, height: 4, borderRadius: 2, backgroundColor: '#E0E0E0', alignSelf: 'center', marginBottom: 14 },
  pickerTitle: { fontSize: 18, fontWeight: '700', color: '#1A1A1A', textAlign: 'center' },
  pickerSub: { fontSize: 13, color: '#777', textAlign: 'center', marginTop: 4, marginBottom: 18 },
  tileGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', rowGap: 14 },
  tileWrap: { width: '31%', alignItems: 'center' },
  tile: {
    width: '100%', aspectRatio: 0.8, borderRadius: 14,
    borderWidth: 1, borderColor: '#E5E5E5',
    alignItems: 'center', justifyContent: 'center',
  },
  tileActive: { borderColor: GOLD, borderWidth: 3 },
  tileCheckWrap: {
    width: 26, height: 26, borderRadius: 13,
    backgroundColor: 'rgba(255,255,255,0.85)',
    alignItems: 'center', justifyContent: 'center',
  },
  tileCheck: { fontSize: 15, fontWeight: '800' },
  tileLabel: { fontSize: 12, color: '#666', marginTop: 6, fontWeight: '500' },
  tileLabelActive: { color: '#1A1A1A', fontWeight: '700' },
  generateBtn: {
    backgroundColor: '#1A1A1A', borderRadius: 100,
    paddingVertical: 15, alignItems: 'center',
    marginTop: 22,
  },
  generateBtnText: { color: '#FFF', fontSize: 15, fontWeight: '700', letterSpacing: 0.5 },
  pickerCancel: { paddingVertical: 12, alignItems: 'center', marginTop: 4 },
  pickerCancelText: { color: '#999', fontSize: 14, fontWeight: '600' },
});
